/**
 * Page guides — "what does this page actually do?"
 *
 * The sidebar already carried a one-line `desc` per item (menu.js). That is
 * enough to CHOOSE a page from a list, but not enough to understand it once
 * you are standing on it: admins still had to click every button to find out
 * what it did. This module adds the long form — a full explanation of every
 * page — and is consumed in two places:
 *
 *   1. sidebar hover  → rich tooltip (title + tagline + top actions)
 *   2. page header    → the small (i) button, which opens the full guide
 *
 * The prose lives in ./pageGuidesData.js, keyed by route. This file only
 * resolves a route to a guide and merges it with the menu entry (label, icon,
 * group) so a page's title is never written down twice.
 */

import { PAGE_GUIDES } from './pageGuidesData.js';
import { MENU_GROUPS, itemMatchesPath } from '../components/Layout/Dashboard/menu.js';

/** '/VoucherCards/Edit/3' → 'vouchercards' — guides are keyed by the root segment. */
function slugOf(path) {
    return String(path || '')
        .replace(/^\//, '')
        .split('/')[0]
        .split('?')[0]
        .toLowerCase();
}

/** Guide prose for a route, or null when the page has not been documented yet. */
export function guideDataFor(path) {
    const slug = slugOf(path);
    if (!slug) return null;
    const keys = Object.keys(PAGE_GUIDES);
    for (let i = 0; i < keys.length; i++) {
        if (slugOf(keys[i]) === slug) return PAGE_GUIDES[keys[i]];
    }
    return null;
}

/**
 * The menu entry for a route (first match across every group, ignoring roles —
 * the label/icon are the same whoever is looking, and role filtering already
 * happened when the sidebar was rendered).
 */
export function menuEntryFor(path) {
    for (let g = 0; g < MENU_GROUPS.length; g++) {
        const group = MENU_GROUPS[g];
        for (let i = 0; i < group.items.length; i++) {
            if (itemMatchesPath(group.items[i], path)) {
                return { item: group.items[i], groupLabel: group.label, groupIcon: group.icon };
            }
        }
    }
    return null;
}

/**
 * Everything the tooltip and the modal need for a route.
 * Returns null only for routes that are neither in the menu nor documented
 * (login, ComingSoon, …) — callers use that to hide the header bar entirely.
 */
export function guideFor(path) {
    const entry = menuEntryFor(path);
    const data = guideDataFor(path);
    if (!entry && !data) return null;

    const item = entry ? entry.item : {};
    return {
        path: path,
        title: item.label || (data && data.title) || '',
        icon: item.icon || 'solar:document-text-line-duotone',
        groupLabel: entry ? entry.groupLabel : '',
        // The menu's one-liner is the fallback tagline, so an undocumented page
        // still shows something useful instead of an empty tooltip.
        tagline: (data && data.tagline) || item.desc || '',
        what: (data && data.what) || '',
        shows: (data && data.shows) || [],
        actions: (data && data.actions) || [],
        notes: (data && data.notes) || [],
        related: (data && data.related) || [],
        documented: !!data
    };
}

/** Related pages, with their menu labels filled in so links never go stale. */
export function relatedLinks(guide) {
    if (!guide || !guide.related) return [];
    return guide.related.map(rel => {
        const entry = menuEntryFor(rel.path);
        return {
            path: rel.path,
            label: (entry && entry.item.label) || rel.label || rel.path,
            icon: (entry && entry.item.icon) || 'solar:arrow-left-line-duotone'
        };
    });
}
